// Notifications inbox — iter64.
//
// Spec:
//   • Sticky page header (#0B0B0B) that stays visible while scrolling.
//   • Day-grouped sections (Today / Yesterday / Earlier).
//   • Per-card X button to dismiss (DELETE /notifications/{id}).
//   • Tapping the card body opens a detail popup (#171717 opaque + X).
//   • Empty state: gold bell icon + "You are all caught up".
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Bell, Star, ShieldCheck, Building2, Store, User,
  Megaphone, AlertTriangle, X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BottomNav } from "../components/BottomNav";
import { useI18n } from "../lib/i18n";

const ICONS = {
  friend_request: User, friend_connected: User,
  business_approved: Building2, vendor_approved: Store,
  review_reminder: Star, reactivation_reminder: ShieldCheck,
  havana_update: Megaphone,
};

const EXCLUDED = new Set(["inactivity_restored_business", "inactivity_restored_vendor", "review_reminder_auto"]);

function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x.getTime(); }
function dayBucket(iso) {
  const today = startOfDay(new Date());
  const yest = today - 86400000;
  const t = startOfDay(iso);
  if (t === today) return "today";
  if (t === yest) return "yesterday";
  return "earlier";
}

export default function Notifications() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get("filter");

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (!cancelled) setItems((data.items || []).filter((n) => !EXCLUDED.has(n.type)));
      } catch { /* ignore */ }
    };
    fetchOnce();
    return () => { cancelled = true; };
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems((data.items || []).filter((n) => !EXCLUDED.has(n.type)));
    } catch { /* ignore */ }
  };

  // setDetail is invoked directly from onClick handlers (see usage below).
  // Helper kept inline to satisfy the React-19 strict lint rule that
  // misclassifies any wrapper as an "effect" path.

  const dismiss = async (e, n) => {
    e.stopPropagation();
    setItems((prev) => prev.filter((x) => x.id !== n.id));
    try { await api.delete(`/notifications/${n.id}`); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); load(); }
  };

  const readAll = async () => {
    try { await api.post("/notifications/read-all"); load(); } catch { /* ignore */ }
  };

  const followAction = (n) => {
    const d = n.meta || n.data || {};
    setDetail(null);
    if (n.type === "havana_update" && (n.update_id || d.update_id)) {
      navigate(`/updates/${n.update_id || d.update_id}`); return;
    }
    if (n.type === "friend_request") { navigate("/friends"); return; }
    if (n.type === "friend_connected" && d.friend_user_id) {
      navigate(`/friends/${d.friend_user_id}`); return;
    }
  };

  const groups = useMemo(() => {
    const g = { today: [], yesterday: [], earlier: [] };
    const filtered = filter === "promotions"
      ? items.filter((n) => (n.type || "").toLowerCase() === "business_promotion")
      : items;
    for (const n of filtered) {
      const b = dayBucket(n.created_at || new Date().toISOString());
      g[b].push(n);
    }
    return g;
  }, [items, filter]);

  const visibleCount = groups.today.length + groups.yesterday.length + groups.earlier.length;
  const unread = items.filter((n) => !n.read).length;

  return (
    <main data-testid="notifications-page" className="min-h-screen pb-32 bg-background">
      {/* Sticky header — never disappears on scroll */}
      <header
        data-testid="notifications-header"
        className="px-5 pt-6 pb-3 flex items-center gap-3"
        style={{ position: "sticky", top: 0, zIndex: 40, background: "#0B0B0B", borderBottom: "1px solid rgba(232,184,75,0.15)" }}
      >
        <Link to="/" className="btn-ghost p-2" style={{ minHeight: 48, minWidth: 48 }}>
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5}/>
        </Link>
        <div className="flex-1">
          <div className="eyebrow">{t("notifications_inbox_eyebrow")}</div>
          <h1 className="font-display text-3xl mt-1 text-foreground" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("notifications_title")}
          </h1>
        </div>
        {unread > 0 && (
          <button data-testid="mark-all-read" onClick={readAll} className="btn-ghost text-xs" style={{ minHeight: 48 }}>
            {t("notifications_mark_all_read")}
          </button>
        )}
      </header>

      {visibleCount === 0 ? (
        <section className="px-5 mt-20 flex flex-col items-center text-center" data-testid="notifications-empty">
          <Bell style={{ width: 48, height: 48, color: "#E8B84B" }} strokeWidth={1.5} />
          <div className="mt-4 text-foreground/55" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16 }}>
            {t("notif_empty_caught_up")}
          </div>
        </section>
      ) : (
        <section className="px-5 mt-4 space-y-6">
          {[
            ["today", t("notif_group_today")],
            ["yesterday", t("notif_group_yesterday")],
            ["earlier", t("notif_group_earlier")],
          ].map(([bucket, label]) => groups[bucket].length > 0 && (
            <div key={bucket} data-testid={`notif-group-${bucket}`}>
              <div className="mb-2" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, color: "#B0AAA3", letterSpacing: "1px", textTransform: "uppercase" }}>
                {label}
              </div>
              <ul className="space-y-2">
                {groups[bucket].map((n) => {
                  const Icon = ICONS[n.type] || Bell;
                  return (
                    <li key={n.id} data-testid={`notif-${n.id}`} className="relative">
                      <button
                        onClick={() => {
                          setDetail(n);
                          if (!n.read) api.post(`/notifications/${n.id}/read`).then(load).catch(() => {});
                        }}
                        className={`w-full text-left lux-card p-4 flex items-start gap-3 ${n.read ? "opacity-60" : ""}`}
                        style={{ paddingRight: 52, minHeight: 64 }}
                      >
                        <span className="w-8 h-8 grid place-items-center rounded-full bg-[hsl(var(--surface-elevated))] border border-gold-soft text-gold shrink-0">
                          <Icon className="w-4 h-4" strokeWidth={1.5} />
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-foreground">{n.title || n.message}</div>
                          <div className="text-[11px] font-mono text-foreground/45 mt-1">{(n.created_at || "").slice(0, 19).replace("T", " · ")}</div>
                        </div>
                        {!n.read && <span className="w-2 h-2 rounded-full bg-gold mt-2 shrink-0" />}
                      </button>
                      <button
                        type="button"
                        onClick={(e) => dismiss(e, n)}
                        data-testid={`notif-dismiss-${n.id}`}
                        aria-label={t("notif_dismiss_aria")}
                        className="absolute top-2.5 right-2.5 grid place-items-center rounded-full hover:bg-white/[0.05] transition-colors"
                        style={{ width: 32, height: 32, color: "#B0AAA3" }}
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </section>
      )}

      {/* Detail popup */}
      {detail && (
        <>
          <button type="button" aria-label="Close"
            onClick={() => setDetail(null)}
            className="fixed inset-0 z-[80]"
            style={{ background: "rgba(0,0,0,0.85)" }} />
          <div
            data-testid="notif-detail-popup"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-[81] w-[92vw] max-w-md p-6"
            style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 20 }}
          >
            <button
              onClick={() => setDetail(null)}
              data-testid="notif-detail-close"
              aria-label={t("notif_detail_close")}
              className="absolute top-3 right-3 grid place-items-center rounded-full hover:bg-white/[0.05]"
              style={{ width: 32, height: 32, color: "#B0AAA3" }}
            >
              <X className="w-4 h-4" strokeWidth={1.5} />
            </button>
            <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8", lineHeight: 1.2, paddingRight: 32 }}>
              {detail.title || detail.message}
            </h2>
            {detail.body && (
              <p className="mt-3 text-foreground/65" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1.5 }}>
                {detail.body}
              </p>
            )}
            {detail.message && detail.title && detail.message !== detail.title && (
              <p className="mt-3 text-foreground/65" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, lineHeight: 1.5 }}>
                {detail.message}
              </p>
            )}
            <div className="mt-4 text-[11px] font-mono text-foreground/45">
              {(detail.created_at || "").slice(0, 19).replace("T", " · ")}
            </div>
            {(detail.type === "friend_connected" || detail.type === "havana_update" || detail.type === "friend_request") && (
              <button
                onClick={() => followAction(detail)}
                data-testid="notif-detail-action"
                className="btn-gold w-full mt-5 py-3 text-xs"
                style={{ minHeight: 48 }}
              >
                Open
              </button>
            )}
          </div>
        </>
      )}

      <BottomNav />
    </main>
  );
}
